import React, { Suspense, useCallback, useEffect } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type AIEventsInstrumentationConfig,
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import { AUDIO_CONTAINER_MODES } from '../../../types/audioContainerMode';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { EmojiProviderContext } from '../../context/EmojiProviderContext';
import { type FlagsContainer, FlagsProviderContext } from '../../context/FlagsProviderContext';
import { getAIFeatureName } from '../../effects/getAIFeatureName';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useMouseOverWithDelayedExit } from '../../hooks/useMouseOverWithDelayedExit';

import type { AudioContainerQuery } from './__generated__/AudioContainerQuery.graphql';
import { AutoPlayManager } from './AutoPlayManager';
import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';
import { useAudioEscapeShortcut } from './useAudioEscapeShortcut';

const styles = cssMap({
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
	emojiProvider: EmojiProvider;
	flags: FlagsContainer;
	pinToExpandedView?: boolean;
	closeFn?: () => void;
	userId?: string;
	displayName?: string;
};

const isFocusInsideContainer = () => {
	const containerElement = document.querySelector('[data-testId="audio-container"]');

	if (!containerElement) {
		return false;
	}
	// document.activeElement is the currently focused element in the entire document
	const focusedElement = document.activeElement;

	// Check if the container itself or any of its descendants has focus
	const isInside = containerElement.contains(focusedElement);

	// Return the result
	return isInside;
};

const AudioContainerComponent = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
	emojiProvider,
	flags,
	pinToExpandedView = false,
	closeFn,
	userId,
	displayName,
}: AudioContainerProps): React.JSX.Element => {
	const {
		activeAudioContainerMode,
		activeContent,
		activeMenu,
		isPlaying,
		pendingContent,
		playImmediately,
		resetPlayImmediately,
		setCustomization,
		setActiveAudioContainerMode,
		updatePlayerState,
	} = useAudioPlayerStore();

	const handleClose = useCallback(() => {
		if (closeFn && isFocusInsideContainer()) {
			closeFn();
		}
	}, [closeFn]);

	//links escape key to close the dialog, but only when the pop-up has focus
	useAudioEscapeShortcut(handleClose);

	// Handle blur event when an element inside the container loses focus
	const handleBlur = () => {
		handleMouseLeave();
	};

	// Handle focus event when an element inside the container receives focus
	const handleFocus = () => {
		handleMouseEnter();
	};

	const handleMouseOverChange = useCallback(
		(isHovered: boolean) => {
			if (!isPlaying && activeAudioContainerMode === AUDIO_CONTAINER_MODES.EXPANDED) {
				return;
			}
			setActiveAudioContainerMode(
				//prevents minification if the popup is hovered or focused
				isHovered || isFocusInsideContainer()
					? AUDIO_CONTAINER_MODES.EXPANDED
					: AUDIO_CONTAINER_MODES.MINIFIED,
			);
		},
		[setActiveAudioContainerMode, isPlaying, activeAudioContainerMode],
	);

	const { handleMouseEnter, handleMouseLeave } = useMouseOverWithDelayedExit({
		delay: 3000,
		initiallyHovered: true,
		resetKey: pendingContent?.contentId,
		isPinned: pinToExpandedView || !!activeMenu,
		onMouseOverChange: handleMouseOverChange,
	});

	useEffect(() => {
		updatePlayerState({
			product,
			cloudId,
			activationId,
		});
	}, [product, cloudId, activationId, updatePlayerState]);

	const { audioRef, fetchAudio } = useAudioPlayerStreaming({
		locale: 'en-US',
		format: AudioFormat.MP3,
		product,
		cloudId,
		activationId,
	});

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const handleBuffer = useCallback(() => {
		trackAIResultAction('audioContentBuffer');
	}, [trackAIResultAction]);

	useEffect(() => {
		if (playImmediately) {
			fetchAudio();
			resetPlayImmediately();
		}
	}, [playImmediately, fetchAudio, resetPlayImmediately]);

	useAudioPlaybackSync({ audioRef, onBuffer: handleBuffer });

	const environment = useRelayEnvironment();

	useEffect(() => {
		const fetchAudioPreference = async () => {
			try {
				const data = await fetchQuery<AudioContainerQuery>(
					environment,
					graphql`
						query AudioContainerQuery($cloudId: ID!) {
							confluence_getAudioPreference(cloudId: $cloudId) {
								tone
								length
								playbackSpeed
							}
						}
					`,
					{ cloudId },
				).toPromise();

				if (data) {
					const tone = data?.confluence_getAudioPreference?.tone;
					const length = data?.confluence_getAudioPreference?.length;
					const playbackSpeed = data?.confluence_getAudioPreference?.playbackSpeed;

					if (tone && length && playbackSpeed) {
						setCustomization({
							tone: tone,
							length: length,
							playbackSpeed: playbackSpeed,
						});
						if (audioRef.current) {
							audioRef.current.defaultPlaybackRate = playbackSpeed;
						}
					} else {
						// TODO: CCI-13829 Handle error appropriately
					}
				}
			} catch {
				// TODO: CCI-13829 Handle error appropriately
			}
		};

		void fetchAudioPreference();
	}, [audioRef, environment, cloudId, setCustomization]);

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio} audioRef={audioRef}>
			{/* Enable auto-play of next track when current track ends */}
			<Suspense fallback={null}>
				<AutoPlayManager cloudId={cloudId} />
			</Suspense>
			<EmojiProviderContext.Provider value={{ emojiProvider }}>
				<FlagsProviderContext.Provider value={{ flags }}>
					<Box
						xcss={styles.container}
						style={{ width: `${activeAudioContainerMode.width}px` }}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						onFocus={handleFocus}
						onBlur={handleBlur}
						testId="audio-container"
					>
					{activeAudioContainerMode === AUDIO_CONTAINER_MODES.EXPANDED ? (
						<ExpandedAudioPlayer
							audioFeatures={audioFeatures}
							userId={userId}
							displayName={displayName}
						/>
					) : (
							<MinifiedAudioPlayer
								title={activeContent.title || 'Untitled'}
								byline={activeContent.author || ''}
							/>
						)}
						<audio ref={audioRef} />
					</Box>
				</FlagsProviderContext.Provider>
			</EmojiProviderContext.Provider>
		</AudioPlayerStreamingProvider>
	);
};

export const AudioContainer = (passThroughProps: AudioContainerProps): JSX.Element => {
	const { activeFeature, closeAudioPlayer } = useAudioPlayerStore();

	const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
		product: passThroughProps.product,
		subproduct: 'platform',
		aiFeatureName: getAIFeatureName(activeFeature),
		proactiveGeneratedAI: 0 as const,
		userGeneratedAI: 1 as const,
		enablePlatformEvents: true,
		skipAISessionValidation: true,
	};

	return (
		<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
			<AudioContainerComponent {...passThroughProps} closeFn={closeAudioPlayer} />
		</AIEventsInstrumentationProvider>
	);
};
